import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  Rating,
  IconButton,
  InputAdornment,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ShoppingCart as CartIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  AddShoppingCart as AddCartIcon,
} from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import MarketplaceService from "../services/MarketplaceService";

const MarketplacePage = () => {
  const { currentUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [error, setError] = useState("");

  // Use MUI theme and responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Categories for filtering
  const categories = [
    "All",
    "Fertilizer",
    "Tools",
    "Seeds",
    "Pesticides",
    "Equipment",
  ];

  useEffect(() => {
    // Load products from API
    const fetchProducts = async () => {
      setLoading(true);
      setError("");
      try {
        const productsData = await MarketplaceService.getAllProducts();
        setProducts(productsData);

        // Load cart from localStorage (cart management stays local for now)
        const savedCart = MarketplaceService.getCart();
        setCart(savedCart);
      } catch (error) {
        console.error("Error loading products:", error);
        setError("Failed to load products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    MarketplaceService.saveCart(cart);
  }, [cart]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = async (category) => {
    setSelectedCategory(category);
    setLoading(true);
    setError("");

    try {
      let productsData;
      if (category === "All") {
        productsData = await MarketplaceService.getAllProducts();
      } else {
        productsData = await MarketplaceService.getProductsByCategory(category);
      }
      setProducts(productsData);
    } catch (error) {
      console.error("Error filtering products:", error);
      setError("Failed to filter products. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    if (!searchTerm.trim()) {
      // If search is empty, reload products based on current category
      if (selectedCategory === "All") {
        const fetchAllProducts = async () => {
          try {
            const productsData = await MarketplaceService.getAllProducts();
            setProducts(productsData);
          } catch (error) {
            console.error("Error loading products:", error);
          }
        };
        fetchAllProducts();
      }
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const productsData = await MarketplaceService.searchProducts(
          searchTerm
        );
        setProducts(productsData);
      } catch (error) {
        console.error("Error searching products:", error);
        setError("Search failed. Please try again.");
      } finally {
        setLoading(false);
      }
    }, 500); // Debounce search by 500ms

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory]);

  const handleOpenProductDialog = (product) => {
    setSelectedProduct(product);
    setQuantity(1);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProduct(null);
    setQuantity(1);
  };

  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value);
    if (value > 0 && value <= selectedProduct.stock) {
      setQuantity(value);
    }
  };

  const handleAddToCart = () => {
    try {
      const updatedCart = MarketplaceService.addToCart(
        selectedProduct,
        quantity
      );
      setCart(updatedCart);
      setOrderSuccess(true);

      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseDialog();
        setOrderSuccess(false);
      }, 1500);
    } catch (error) {
      console.error("Error adding to cart:", error);
      setError("Failed to add item to cart");
    }
  };

  // Products are already filtered by the API, so use them directly
  const filteredProducts = products;

  const getTotalCartItems = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalCartPrice = () => {
    return cart
      .reduce((total, item) => total + item.price * item.quantity, 0)
      .toFixed(2);
  };

  // Format price to display RM instead of $
  const formatPrice = (price) => {
    // Convert string to number if needed
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return `RM ${numPrice.toFixed(2)}`;
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, mb: 8 }}>
      <Typography variant="h5" gutterBottom>
        Marketplace
      </Typography>

      {/* Search and Filter */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size={isMobile ? "small" : "medium"}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: isMobile ? "center" : "flex-start",
              }}
            >
              {categories.map((category) => (
                <Chip
                  key={category}
                  label={category}
                  onClick={() => handleCategoryChange(category)}
                  color={selectedCategory === category ? "primary" : "default"}
                  variant={
                    selectedCategory === category ? "filled" : "outlined"
                  }
                  size={isMobile ? "small" : "medium"}
                  sx={{ mb: 1 }}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Shopping Cart Summary */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? 1 : 0,
        }}
      >
        <Box>
          <Typography variant="subtitle1">
            Your Cart: {getTotalCartItems()} items
          </Typography>
          <Typography variant="h6" color="primary">
            Total: {formatPrice(getTotalCartPrice())}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<CartIcon />}
          disabled={cart.length === 0}
          fullWidth={isMobile}
          size={isMobile ? "small" : "medium"}
        >
          Checkout
        </Button>
      </Paper>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Products Grid */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredProducts.length === 0 ? (
        <Box sx={{ textAlign: "center", my: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filteredProducts.map((product) => (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardMedia
                  component="img"
                  height={isMobile ? "140" : "200"}
                  image={product.imageUrl}
                  alt={product.name}
                />
                <CardContent sx={{ flexGrow: 1, p: isMobile ? 1.5 : 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      component="div"
                      gutterBottom
                    >
                      {product.name}
                    </Typography>
                    <Chip
                      label={product.category}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Rating
                      value={product.rating}
                      precision={0.5}
                      readOnly
                      size="small"
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      ({product.rating})
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      mb: 2,
                    }}
                  >
                    {product.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      color="primary"
                    >
                      {formatPrice(product.price)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.stock} in stock
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ p: isMobile ? 1 : 1.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() => handleOpenProductDialog(product)}
                    startIcon={<AddCartIcon />}
                    size={isMobile ? "small" : "medium"}
                  >
                    Add to Cart
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Product Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        {selectedProduct && (
          <>
            <DialogTitle>{selectedProduct.name}</DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <img
                    src={selectedProduct.imageUrl}
                    alt={selectedProduct.name}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      maxHeight: isMobile ? "200px" : "300px",
                      objectFit: "cover",
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      gutterBottom
                    >
                      {selectedProduct.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Rating
                        value={selectedProduct.rating}
                        precision={0.5}
                        readOnly
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({selectedProduct.rating})
                      </Typography>
                    </Box>
                    <Chip
                      label={selectedProduct.category}
                      size="small"
                      color="primary"
                      sx={{ mb: 2 }}
                    />
                    <Typography variant="body1" paragraph>
                      {selectedProduct.description}
                    </Typography>
                    <Typography
                      variant={isMobile ? "h6" : "h5"}
                      color="primary"
                      gutterBottom
                    >
                      {formatPrice(selectedProduct.price)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedProduct.stock} {selectedProduct.unit}s in stock
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Weight: {selectedProduct.weight} per{" "}
                      {selectedProduct.unit}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography variant="body1" sx={{ mr: 2 }}>
                      Quantity:
                    </Typography>
                    <TextField
                      type="number"
                      value={quantity}
                      onChange={handleQuantityChange}
                      inputProps={{ min: 1, max: selectedProduct.stock }}
                      size="small"
                      sx={{ width: "80px" }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 2 }}
                    >
                      ({selectedProduct.unit}s)
                    </Typography>
                  </Box>

                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    gutterBottom
                  >
                    Total: {formatPrice(selectedProduct.price * quantity)}
                  </Typography>

                  {orderSuccess && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      Added to cart successfully!
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddToCart}
                startIcon={<AddCartIcon />}
              >
                Add to Cart
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default MarketplacePage;
